import React, { useState, useCallback } from 'react';
import { View, Modal, Pressable, Image, Platform, StyleSheet, Dimensions } from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { extractVideoMetadata, getYouTubeEmbedUrl, getVimeoEmbedUrl, type VideoProvider } from '@utils/videoUtils';
import YoutubePlayer from 'react-native-youtube-iframe';
import { SizableText, YStack } from 'tamagui';

interface VideoPlayerProps {
  url: string;
  thumbnail?: string;
  title?: string;
  provider?: VideoProvider;
  videoId?: string;
  style?: any;
  height?: number;
  autoPlay?: boolean;
  onPlayStateChange?: (isPlaying: boolean) => void;
  inline?: boolean; // If true, plays inline instead of modal
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url,
  thumbnail,
  title,
  provider: providedProvider,
  videoId: providedVideoId,
  style,
  height = 300,
  autoPlay = false,
  onPlayStateChange,
  inline = true, // Default to inline playback
}) => {
  const theme = themeSelector();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [showPlayer, setShowPlayer] = useState(autoPlay && inline); // Show player immediately if autoplay and inline

  // Extract metadata if not provided
  const metadata = extractVideoMetadata(url);
  const videoProvider = providedProvider || metadata?.provider;
  const videoId = providedVideoId || metadata?.videoId;
  const videoThumbnail = thumbnail || metadata?.thumbnail;
  const videoTitle = title || metadata?.title;

  const handleThumbnailPress = useCallback(() => {
    if (inline) {
      // Inline mode: show player in place
      setShowPlayer(true);
      setIsPlaying(true);
      onPlayStateChange?.(true);
    } else {
      // Modal mode: open modal
      setIsModalVisible(true);
      setIsPlaying(true);
      onPlayStateChange?.(true);
    }
  }, [inline, onPlayStateChange]);


  const handleCloseModal = useCallback(() => {
    setIsModalVisible(false);
    setIsPlaying(false);
    onPlayStateChange?.(false);
  }, [onPlayStateChange]);

  const handleStateChange = useCallback((state: string) => {
    if (state === 'playing') {
      setIsPlaying(true);
      onPlayStateChange?.(true);
    } else if (state === 'paused' || state === 'ended') {
      setIsPlaying(false);
      onPlayStateChange?.(false);
    }
  }, [onPlayStateChange]);

  if (!videoProvider || !videoId) {
    return null;
  }

  const renderEmbed = (inlineHeight?: number) => {
    if (videoProvider === 'youtube') {
      // Use YoutubeIframe component which supports play prop on both web and native
      const embedHeight = inline && inlineHeight ? inlineHeight : Dimensions.get('window').height * 0.7;
      return (
        <YoutubePlayer
          height={embedHeight}
          play={isPlaying}
          videoId={videoId}
          onChangeState={handleStateChange}
          webViewStyle={{ borderRadius: 0 }}
        />
      );
    }

    if (videoProvider === 'vimeo') {
      const embedUrl = getVimeoEmbedUrl(videoId, isPlaying);
      const embedHeight = inline && inlineHeight ? inlineHeight : Dimensions.get('window').height * 0.7;
      return (
        <iframe
          src={embedUrl}
          width="100%"
          height={embedHeight ? `${embedHeight}px` : '100%'}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          style={{ border: 'none', borderRadius: 12 }}
        />
      );
    }

    // TikTok - redirect to TikTok
    if (videoProvider === 'tiktok') {
      return (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <SizableText style={{ color: theme ? '#000' : '#fff', marginBottom: 20, textAlign: 'center' }}>
            TikTok videos must be viewed on TikTok
          </SizableText>
          <Pressable
            onPress={() => {
              if (Platform.OS === 'web') {
                window.open(url, '_blank');
              }
              handleCloseModal();
            }}
            style={{
              backgroundColor: '#000',
              paddingHorizontal: 24,
              paddingVertical: 12,
              borderRadius: 8,
            }}
          >
            <SizableText style={{ color: '#fff', fontWeight: '600' }}>Open in TikTok</SizableText>
          </Pressable>
        </View>
      );
    }

    return null;
  };

  return (
    <View style={[{ width: '100%', position: 'relative' }, style]}>
      {/* Show player inline if inline mode and showPlayer is true */}
      {inline && showPlayer && (videoProvider === 'youtube' || videoProvider === 'vimeo') ? (
        <View style={[styles.thumbnailContainer, { height, backgroundColor: '#000', overflow: 'hidden' }]}>
          {renderEmbed(height)}
        </View>
      ) : (
        /* Thumbnail Preview */
        <Pressable
          onPress={handleThumbnailPress}
          style={[
            styles.thumbnailContainer,
            {
              height,
              backgroundColor: theme ? '#f0f0f0' : '#2a2a2a',
            },
          ]}
        >
          {videoThumbnail ? (
            <Image
              source={{ uri: videoThumbnail }}
              style={styles.thumbnailImage}
              resizeMode="cover"
            />
          ) : (
            <View style={[styles.thumbnailPlaceholder, { backgroundColor: theme ? '#e0e0e0' : '#3a3a3a' }]}>
              <FontAwesome name="video-camera" size={48} color={theme ? '#999' : '#666'} />
            </View>
          )}

          {/* Play Icon Overlay */}
          <View style={styles.playIconOverlay} pointerEvents="none">
            <View
              style={[
                styles.playIconContainer,
                {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              ]}
            >
              <FontAwesome name="play" size={32} color="#fff" style={{ marginLeft: 4 }} />
            </View>
          </View>

          {/* Provider Badge */}
          <View
            style={[
              styles.providerBadge,
              {
                backgroundColor: videoProvider === 'youtube' ? '#FF0000' : videoProvider === 'tiktok' ? '#000' : '#1AB7EA',
              },
            ]}
            pointerEvents="none"
          >
            <SizableText style={{ color: '#fff', fontSize: 10, fontWeight: '700' }}>
              {videoProvider.toUpperCase()}
            </SizableText>
          </View>

          {/* Title Overlay (optional) */}
          {videoTitle && (
            <View style={styles.titleOverlay} pointerEvents="none">
              <SizableText
                numberOfLines={2}
                style={{
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: '600',
                  textShadowColor: 'rgba(0, 0, 0, 0.8)',
                  textShadowRadius: 4,
                  textShadowOffset: { width: 0, height: 1 },
                }}
              >
                {videoTitle}
              </SizableText>
            </View>
          )}
        </Pressable>
      )}

      {/* Full Screen Modal - Only show if not inline */}
      {!inline && (
        <Modal
          visible={isModalVisible}
          animationType="slide"
          transparent={false}
          onRequestClose={handleCloseModal}
          supportedOrientations={['portrait', 'landscape']}
        >
          <View style={styles.modalContainer}>
            {/* Close Button */}
            <Pressable
              onPress={handleCloseModal}
              style={[
                styles.closeButton,
                {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              ]}
            >
              <FontAwesome name="times" size={24} color="#fff" />
            </Pressable>

            {/* Video Embed */}
            <View style={styles.videoContainer}>
              {renderEmbed(undefined)}
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  thumbnailContainer: {
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
  },
  thumbnailPlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  playIconOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  playIconContainer: {
    width: 72,
    height: 72,
    borderRadius: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  providerBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  titleOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 12,
    backgroundColor: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 50 : 20,
    right: 20,
    zIndex: 1000,
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoContainer: {
    width: '100%',
    height: Dimensions.get('window').height,
    maxWidth: Platform.OS === 'web' ? 1200 : '100%',
    maxHeight: Platform.OS === 'web' ? 675 : '100%',
    aspectRatio: 16 / 9,
  },
});

export default VideoPlayer;

